// Native touches for installed apps: share sheet, app icon badge, haptics (DevRobot UI kit).
// Everything degrades quietly where a platform doesn't offer it.

/** Running from the Home Screen / as an installed app, not in a browser tab. */
export const isStandalone = () =>
  (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * An installed app in a desktop window -- today that means Chromium, the only desktop family
 * that installs one. This is the one place where leaving the app is a round trip rather than a
 * departure: the window *is* the app, so it can carry you to an application host and bring you
 * back, where a browser tab would strand a second tab behind. It is also the one place with no
 * browser chrome, so nothing but the app itself can offer the way back.
 *
 * Phones and tablets are ruled out by the pointer, not by the user agent: an iOS Home Screen app
 * has a coarse pointer and no hover, and it already has the system's own edge-swipe to go back.
 */
export const isDesktopPwa = () => isStandalone() && typeof matchMedia === 'function' && matchMedia('(pointer: fine) and (hover: hover)').matches;

/** The system share sheet. Resolves false where there isn't one (callers copy the link instead). */
export async function share(data: { title?: string; text?: string; url?: string }): Promise<boolean> {
  if (!navigator.share) return false;
  try {
    await navigator.share(data);
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') return false;
  }
  return true;
}

/* ── App icon badge ─────────────────────────────────────────────────────── */
// On iOS the badge exists only for Home Screen apps, and only after notification permission.

const BADGE_KEY = 'devrobot:badge';
type BadgeNavigator = Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
const nav = () => navigator as BadgeNavigator;

export const badgeSupported = () => typeof navigator !== 'undefined' && 'setAppBadge' in navigator;

/** "granted" | "denied" | "default", or "unsupported" (e.g. Safari in a tab: add to Home Screen first). */
export const badgePermission = (): NotificationPermission | 'unsupported' => (badgeSupported() && 'Notification' in window ? Notification.permission : 'unsupported');

/** Ask for permission; call it from a tap. Resolves to the resulting permission. */
export async function requestBadgePermission(): Promise<NotificationPermission | 'unsupported'> {
  if (badgePermission() === 'unsupported') return 'unsupported';
  return Notification.requestPermission();
}

export function badgeEnabled(): boolean {
  try {
    return localStorage.getItem(BADGE_KEY) === 'on' && badgePermission() === 'granted';
  } catch {
    return false;
  }
}

export function setBadgeEnabled(on: boolean) {
  try {
    localStorage.setItem(BADGE_KEY, on ? 'on' : 'off');
  } catch {
    /* storage unavailable: the choice lasts for this visit */
  }
  if (!on) clearBadge();
}

/** Show `count` on the app icon (0 clears it). Does nothing unless the person turned badges on. */
export function setBadge(count: number) {
  if (!badgeSupported() || !badgeEnabled()) return;
  (count > 0 ? nav().setAppBadge!(count) : nav().clearAppBadge!()).catch(() => {});
}

export function clearBadge() {
  if (badgeSupported()) nav().clearAppBadge?.().catch(() => {});
}

/* ── Haptics ────────────────────────────────────────────────────────────── */
// Reserved for actions (deploy, redeploy, save, submit), never for navigation or plain toggles.
// iOS has no vibration API. WebKit plays the system tick when a native
// <input type="checkbox" switch> flips, and since iOS 26.5 only when a finger flips it:
// action buttons carry an invisible switch on top (<Haptic> in ./haptics.tsx). Android vibrates.

export const isIOS =
  typeof navigator !== 'undefined' && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

let scriptedSwitch: HTMLLabelElement | undefined;
let lastTick = 0;
let lastError = 0;

/** Record a tick that already played (the <Haptic> switch under a finger), so feedback isn't doubled. */
export const markTick = () => {
  lastTick = Date.now();
};

// A tick from script: plays on iOS 17.4–26.4, silent from 26.5 on.
function scriptedTick() {
  if (!scriptedSwitch?.isConnected) {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    input.tabIndex = -1;
    scriptedSwitch = document.createElement('label');
    scriptedSwitch.setAttribute('aria-hidden', 'true');
    scriptedSwitch.style.cssText = 'position:fixed;left:0;bottom:0;width:1px;height:1px;overflow:hidden;opacity:0.01;pointer-events:none;z-index:-1';
    scriptedSwitch.append(input);
    document.body.append(scriptedSwitch);
  }
  scriptedSwitch.click();
}

function tick() {
  if (isIOS) scriptedTick();
  else if (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) navigator.vibrate?.(10);
}

/**
 * Feedback for an action or its outcome (toasts call it too).
 *   "tap"   — it went through: one tick.
 *   "error" — it was refused or failed: a double tick.
 * Ticks that belong to the same tap merge: a toast right after a <Haptic> button adds nothing,
 * and an error right after one adds only the second tick.
 */
export function haptic(kind: 'tap' | 'error' = 'tap') {
  const now = Date.now();
  const recent = now - lastTick < 450;
  if (kind === 'tap') {
    if (!recent) {
      markTick();
      tick();
    }
    return;
  }
  if (now - lastError < 500) return; // one double tick per refusal (invalid fields + error toast)
  lastError = now;
  if (!recent) tick();
  markTick();
  setTimeout(tick, 120);
}
